// ─────────────────────────────────────────────────────────────────────────────
// Idle-session tracking
//
// A signed-in session survives five hours of doing nothing. Any real activity
// — typing, clicking, scrolling, switching tabs — pushes the deadline back, so
// someone who is working is never logged out. When the deadline does pass,
// every registered "flush" handler runs first, so work in progress is saved
// before the session is torn down.
//
// The activity stamp lives in localStorage rather than in memory so that all
// open tabs of the app agree on one deadline: working in one tab keeps the
// others alive too.
// ─────────────────────────────────────────────────────────────────────────────

export const IDLE_LIMIT_MS = 5 * 60 * 60 * 1000; // 5 hours

const ACTIVITY_KEY = "synthiumLastActivityAt";

/** True when this tab holds a signed-in (or guest) session. */
export const hasActiveSession = () => {
  try {
    return !!(
      sessionStorage.getItem("token") ||
      sessionStorage.getItem("is_guest") === "true" ||
      localStorage.getItem("token")
    );
  } catch {
    return false;
  }
};

export const getLastActivityAt = () => {
  try {
    const raw = Number(localStorage.getItem(ACTIVITY_KEY));
    return Number.isFinite(raw) && raw > 0 ? raw : 0;
  } catch {
    return 0;
  }
};

export const markActivity = (at = Date.now()) => {
  try {
    localStorage.setItem(ACTIVITY_KEY, String(at));
  } catch {
    // Storage unavailable — the in-tab timer still works from its own clock.
  }
};

export const clearActivity = () => {
  try {
    localStorage.removeItem(ACTIVITY_KEY);
  } catch {
    /* ignore */
  }
};

// ── Save-on-timeout registry ─────────────────────────────────────────────────

const saveHandlers = new Set();

/**
 * Registers work that must be flushed before an idle logout — an editor with
 * unsaved changes, a form mid-edit, anything whose state would otherwise be
 * lost when the session is cleared.
 *
 * @param {() => (void | Promise<void>)} handler
 * @returns {() => void} unregister, suitable as a useEffect cleanup
 */
export const registerIdleSaveHandler = (handler) => {
  if (typeof handler !== "function") return () => {};
  saveHandlers.add(handler);
  return () => saveHandlers.delete(handler);
};

/**
 * Runs every registered handler. A handler that hangs or throws must not stop
 * the logout, so each one is isolated and the whole batch is capped.
 *
 * @param {number} timeoutMs how long to wait for the handlers in total
 */
export const flushPendingWork = async (timeoutMs = 8000) => {
  if (!saveHandlers.size) return;

  const runs = Array.from(saveHandlers).map(async (handler) => {
    try {
      await handler();
    } catch (error) {
      console.error("Idle save handler failed:", error);
    }
  });

  await Promise.race([
    Promise.all(runs),
    new Promise((resolve) => setTimeout(resolve, timeoutMs)),
  ]);
};
